import { RepositoryErrors } from 'repository/errors/repository-errors'
import handleGraphQLError from 'repository/graphql/utils/handle-error'
import { PatientNewRepository as IPatientNewRepository } from 'repository/interfaces/patient-new-repository'
import { ApiStatusCode } from 'service/protocols/api/api-client'
import { IApiRepository } from 'service/protocols/api/api-repository'
import { UpdatePatientContact } from 'domain/usecases/patient/update-patient-contact'
import { GetHealthInsuranceByPatient } from 'domain/usecases/patient/get-health-insurance-by-patient'
import { GetAllHealthPlans } from 'domain/usecases/patient/get-all-health-plans'
import { CustomError } from 'common/errors'
import { CreateNewPatient } from 'domain/usecases/patient/create-new-patient'
import { GetHealthSubPlans } from 'domain/usecases/patient/get-health-sub-plans'
import { GetPatients } from 'domain/usecases/patient/get-patient'
import { CreateHealthInsuranceByPatient } from 'domain/usecases/patient/create-health-insuranceby-patient'
import { GetPatientByCpfOrName } from 'domain/usecases/patient/get-patient-by-cpf-or-name'

export class PatientNewRepository implements IPatientNewRepository {
  constructor(private readonly apiRepository: IApiRepository) {}

  async getPatients(params?: GetPatients.Params): Promise<GetPatients.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<GetPatients.Model>

    const httpResponse: any = await apiRepository.get({
      url: '/patient/api/patients/search',
      params
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw new CustomError('Ocorreu um erro ao buscar o paciente')
    } else {
      return httpResponse.body.data as GetPatients.Model
    }
  }

  async getPatientByCpfOrName(
    params?: GetPatientByCpfOrName.Params
  ): Promise<GetPatientByCpfOrName.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<GetPatientByCpfOrName.Model>

    const httpResponse: any = await apiRepository.get({
      url: '/get-patient-in-gateway',
      params
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(
        RepositoryErrors[httpResponse.error! as keyof typeof RepositoryErrors]
      )
    } else {
      return httpResponse.body.data as GetPatientByCpfOrName.Model
    }
  }

  async createNewPatient(
    params: CreateNewPatient.Params
  ): Promise<CreateNewPatient.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CreateNewPatient.Model>

    const httpResponse: any = await apiRepository.post({
      url: '/patient/api/patients',
      body: params
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw handleGraphQLError(
        RepositoryErrors[
          httpResponse.body?.data?.code as keyof typeof RepositoryErrors
        ]
      )
    } else {
      return httpResponse.body.data as CreateNewPatient.Model
    }
  }

  async updatePatientContact(
    params: UpdatePatientContact.Params
  ): Promise<UpdatePatientContact.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<UpdatePatientContact.Model>

    const httpResponse: any = await apiRepository.put({
      url: `/patient/api/patients/${params.patient_id}`,
      body: {
        email: params?.email,
        phone: params?.phone,
        landline: params?.landline
      }
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw handleGraphQLError(
        RepositoryErrors[httpResponse.error! as keyof typeof RepositoryErrors]
      )
    } else {
      return httpResponse.body.data as UpdatePatientContact.Model
    }
  }

  async getHealthInsuranceByPatient(
    params?: GetHealthInsuranceByPatient.Params
  ): Promise<GetHealthInsuranceByPatient.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<GetHealthInsuranceByPatient.Model>

    const httpResponse: any = await apiRepository.get({
      url: `/patient/api/patients/${params?.patient_id}/health-insurances?last_health_insurance=${params?.last_health_insurance}&is_active=${params?.is_active}`
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw new CustomError('Ocorreu um erro ao buscar convênio do paciente')
    } else {
      return httpResponse.body.data as GetHealthInsuranceByPatient.Model
    }
  }

  async getAllHealthPlans(): Promise<GetAllHealthPlans.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<GetAllHealthPlans.Model>

    const httpResponse: any = await apiRepository.get({
      url: `/patient/api/health-plans`
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw new CustomError('Ocorreu um erro ao buscar convênios')
    } else {
      return httpResponse.body.data as GetAllHealthPlans.Model
    }
  }

  async createHealthInsuranceByPatient(
    params: CreateHealthInsuranceByPatient.Params
  ): Promise<CreateHealthInsuranceByPatient.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CreateHealthInsuranceByPatient.Model>

    const {
      patient_id,
      patient_name,
      health_insurance_code,
      health_insurance_name,
      health_plan_code,
      health_plan_name,
      health_sub_plan_code,
      health_sub_plan_name,
      company_name,
      valid_thru,
      health_card
    } = params

    const httpResponse: any = await apiRepository.post({
      url: `/patient/api/patients/${patient_id}/health-insurances`,
      body: {
        patient_name,
        health_insurance_code,
        health_insurance_name,
        health_plan_code,
        health_plan_name,
        health_sub_plan_code,
        health_sub_plan_name,
        company_name,
        valid_thru,
        health_card
      }
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw handleGraphQLError(
        RepositoryErrors[httpResponse.error! as keyof typeof RepositoryErrors]
      )
    } else {
      return httpResponse.body.data as CreateHealthInsuranceByPatient.Model
    }
  }

  async getHealthSubPlans(
    params: GetHealthSubPlans.Params
  ): Promise<GetHealthSubPlans.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<GetHealthSubPlans.Model>

    const httpResponse: any = await apiRepository.get({
      url: `/patient/api/health-plans/sub-plans`,
      params
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw new CustomError('Ocorreu um erro ao buscar sub planos do convênio')
    } else {
      return httpResponse.body.data as GetHealthSubPlans.Model
    }
  }
}
